import { Html, Line } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { X } from 'lucide-react'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { SphereGeometry, Vector3 } from 'three'

const POSITION_UPDATE_THRESHOLD = 0.001

const MeasurementLabel = styled.div<{ $interactive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-family: 'Manrope', sans-serif;
`

const DismissButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  padding: 0;
  margin-left: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;

  svg {
    width: 14px;
    height: 14px;
    color: white;
    stroke: white;
  }

  &:hover {
    background: rgba(255, 100, 100, 0.4);
    border-color: rgba(255, 100, 100, 0.6);

    svg {
      color: #ff6b6b;
      stroke: #ff6b6b;
    }
  }
`

const HoverIndicator = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #00ff88;
  border-radius: 50%;
  background: rgba(0, 255, 136, 0.3);
  pointer-events: none;
  transform: translate(-50%, -50%);
`

interface MeasurementData {
  id: number
  start: Vector3
  end: Vector3
  distance: number
}

interface MeasurementToolProps {
  isActive: boolean
  onMeasurementComplete?: () => void
}

/**
 * Formats a distance value to a human-readable string
 */
function formatDistance(meters: number): string {
  if (meters < 1) {
    return `${(meters * 100).toFixed(1)} cm`
  }
  if (meters < 1000) {
    return `${meters.toFixed(2)} m`
  }
  return `${(meters / 1000).toFixed(3)} km`
}

/**
 * A sphere marker rendered at measurement points
 */
const MeasurementMarker: FC<{ position: Vector3; color: string }> = ({
  position,
  color,
}) => {
  const geometry = useMemo(() => new SphereGeometry(0.15, 16, 16), [])

  return (
    <mesh position={position} geometry={geometry} name='measurement-marker'>
      <meshBasicMaterial color={color} />
    </mesh>
  )
}

/**
 * A completed measurement with line, markers, and dismissable label
 */
const CompletedMeasurement: FC<{
  measurement: MeasurementData
  onDismiss: () => void
}> = ({ measurement, onDismiss }) => {
  const midpoint = useMemo(
    () =>
      new Vector3()
        .addVectors(measurement.start, measurement.end)
        .multiplyScalar(0.5),
    [measurement.start, measurement.end]
  )

  return (
    <group>
      <MeasurementMarker position={measurement.start} color='#00ff88' />
      <MeasurementMarker position={measurement.end} color='#00ff88' />
      <Line
        points={[measurement.start, measurement.end]}
        color='#00ff88'
        lineWidth={2}
        name='measurement-line'
      />
      <Html position={midpoint} center style={{ pointerEvents: 'none' }}>
        <MeasurementLabel $interactive>
          {formatDistance(measurement.distance)}
          <DismissButton onClick={onDismiss} title='Remove measurement'>
            <X strokeWidth={2.5} />
          </DismissButton>
        </MeasurementLabel>
      </Html>
    </group>
  )
}

/**
 * Interactive measurement tool for measuring distances between points on 3D tiles.
 * Click to place the first point, then click again to complete the measurement.
 * Completed measurements stay visible with an X to dismiss them.
 */
export const MeasurementTool: FC<MeasurementToolProps> = ({
  isActive,
  onMeasurementComplete,
}) => {
  const { gl } = useThree()
  const [measurements, setMeasurements] = useState<MeasurementData[]>([])
  const [pendingStart, setPendingStart] = useState<Vector3 | null>(null)
  const [hoverPoint, setHoverPoint] = useState<Vector3 | null>(null)
  const [previewDistance, setPreviewDistance] = useState<number | null>(null)
  const nextIdRef = useRef(0)
  const pendingClickRef = useRef(false)
  const lastHoverPointRef = useRef<Vector3 | null>(null)
  const tempVectorRef = useRef(new Vector3())

  // Pre-allocated vectors for state updates (to avoid new allocations in frame loop)
  const pendingHoverUpdateRef = useRef<{
    x: number
    y: number
    z: number
  } | null>(null)
  const pendingClickPointRef = useRef<{
    x: number
    y: number
    z: number
  } | null>(null)

  // Trigger to force useEffect to process pending ref updates
  const [updateTrigger, setUpdateTrigger] = useState(0)

  // Reset pending measurement when tool is deactivated
  useEffect(() => {
    if (!isActive) {
      setPendingStart(null)
      setHoverPoint(null)
      setPreviewDistance(null)
    }
  }, [isActive])

  // Use R3F's built-in pointer tracking for hover and handle clicks
  useFrame((state) => {
    if (!isActive) return

    state.raycaster.setFromCamera(state.pointer, state.camera)
    const intersects = state.raycaster.intersectObjects(
      state.scene.children,
      true
    )

    // Find first valid intersection (skip measurement objects)
    const validHit = intersects.find(
      (hit) =>
        hit.object.name !== 'measurement-marker' &&
        hit.object.name !== 'measurement-line'
    )

    if (validHit) {
      // Use temp vector to avoid cloning every frame
      const hitPoint = tempVectorRef.current.copy(validHit.point)

      // Only update state if position changed significantly (prevents blinking)
      const lastPoint = lastHoverPointRef.current
      const shouldUpdate =
        !lastPoint || hitPoint.distanceTo(lastPoint) > POSITION_UPDATE_THRESHOLD

      if (shouldUpdate) {
        // Store coordinates for state update (handled outside frame loop)
        pendingHoverUpdateRef.current = {
          x: hitPoint.x,
          y: hitPoint.y,
          z: hitPoint.z,
        }
        // Trigger useEffect to process the update
        setUpdateTrigger((t) => t + 1)
      }

      // Handle pending click
      if (pendingClickRef.current) {
        pendingClickRef.current = false
        pendingClickPointRef.current = {
          x: hitPoint.x,
          y: hitPoint.y,
          z: hitPoint.z,
        }
        // Trigger useEffect to process the click
        setUpdateTrigger((t) => t + 1)
      }
    } else {
      if (lastHoverPointRef.current !== null) {
        lastHoverPointRef.current = null
        pendingHoverUpdateRef.current = null
        setHoverPoint(null)
        if (!pendingStart) {
          setPreviewDistance(null)
        }
      }
    }
  })

  // Process pending updates outside of frame loop
  useEffect(() => {
    // Handle hover update
    if (pendingHoverUpdateRef.current) {
      const { x, y, z } = pendingHoverUpdateRef.current
      const newPoint = new Vector3(x, y, z)
      lastHoverPointRef.current = newPoint
      setHoverPoint(newPoint)

      if (pendingStart) {
        const dist = pendingStart.distanceTo(newPoint)
        setPreviewDistance(dist)
      }
      pendingHoverUpdateRef.current = null
    }

    // Handle click
    if (pendingClickPointRef.current) {
      const { x, y, z } = pendingClickPointRef.current
      const clickPoint = new Vector3(x, y, z)
      pendingClickPointRef.current = null

      if (!pendingStart) {
        // First click - set start point
        setPendingStart(clickPoint)
        setPreviewDistance(null)
      } else {
        // Second click - complete measurement
        const distance = pendingStart.distanceTo(clickPoint)
        const newMeasurement: MeasurementData = {
          id: nextIdRef.current++,
          start: pendingStart,
          end: clickPoint,
          distance,
        }
        setMeasurements((prev) => [...prev, newMeasurement])
        setPendingStart(null)
        setPreviewDistance(null)
        onMeasurementComplete?.()
      }
    }
  }, [pendingStart, onMeasurementComplete, updateTrigger])

  // Handle click via DOM - set flag for useFrame to process
  useEffect(() => {
    if (!isActive) return

    const handleClick = () => {
      pendingClickRef.current = true
    }

    gl.domElement.addEventListener('pointerdown', handleClick)
    gl.domElement.style.cursor = 'crosshair'
    return () => {
      gl.domElement.removeEventListener('pointerdown', handleClick)
      gl.domElement.style.cursor = ''
    }
  }, [isActive, gl.domElement])

  const handleDismiss = useCallback((id: number) => {
    setMeasurements((prev) => prev.filter((m) => m.id !== id))
  }, [])

  // Calculate midpoint for preview label
  const previewMidpoint = useMemo(() => {
    if (pendingStart && hoverPoint) {
      return new Vector3()
        .addVectors(pendingStart, hoverPoint)
        .multiplyScalar(0.5)
    }
    return null
  }, [pendingStart, hoverPoint])

  return (
    <group>
      {/* Completed measurements */}
      {measurements.map((m) => (
        <CompletedMeasurement
          key={m.id}
          measurement={m}
          onDismiss={() => handleDismiss(m.id)}
        />
      ))}

      {/* Active measurement in progress */}
      {isActive && pendingStart && (
        <>
          <MeasurementMarker position={pendingStart} color='#00ff88' />
          {hoverPoint && (
            <>
              <Line
                points={[pendingStart, hoverPoint]}
                color='#ffff00'
                lineWidth={2}
                dashed
                dashSize={0.5}
                gapSize={0.25}
                name='measurement-line'
              />
              {previewMidpoint && previewDistance !== null && (
                <Html
                  position={previewMidpoint}
                  center
                  style={{ pointerEvents: 'none' }}>
                  <MeasurementLabel
                    style={{ borderColor: 'rgba(255, 255, 0, 0.5)' }}>
                    {formatDistance(previewDistance)}
                  </MeasurementLabel>
                </Html>
              )}
            </>
          )}
        </>
      )}

      {/* Hover indicator when placing first point */}
      {isActive && !pendingStart && hoverPoint && (
        <Html position={hoverPoint} center style={{ pointerEvents: 'none' }}>
          <HoverIndicator />
        </Html>
      )}
    </group>
  )
}
